<script lang="ts">
	import { onMount } from 'svelte';
		import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		real_id,
		get_gen_2_component_from_category,
		get_gen_2_component_channel_structure,
		get_device_component_index,
	} from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import {
		formatOperationalResetTimestamp,
		formatOperationalRuntimeFull,
		formatOperationalRuntimeSeconds,
		hasActiveLoadRuntimeSupport,
		mergePartialDeviceStatus,
	} from 'src/helpers/operational_counters_helpers';
		import { getDeviceSettings, getDeviceStatus, readDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { format } from 'svelte-i18n';
	import { DEVICE_MAX_POWER_DEFAULTS } from 'src/constants';
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { InputClickEvent } from 'src/types/InputClickEvent';
	import {
		Button,
		Container,
		Input,
		Modal,
		Text,
		Toggle,
		Icon,
		Tooltip,
	} from '@Shelly/shelly-design-system';

	export let device: Device;
	export let isOffline = false;

	type CounterType = 'on_time' | 'switch_on' | 'on_above_thr';

	let resetModalType: CounterType | 'all' | null = null;
	let loadingToggle = false;
	let loadingThreshold = false;
	let loadingReset = false;
	let powerThresholdInput = '';
	let powerThresholdInitialized = false;
	const powerThresholdMin = -2500;

	const realDeviceId = real_id(device.id);
	const comp = get_gen_2_component_from_category(device);
	const componentIndex = get_device_component_index(device);
	const key = get_gen_2_component_channel_structure(device);

	$: statusStore = getDeviceStatus(realDeviceId);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(realDeviceId);
	$: settings = $settingsStore;
	$: statusCounts = status?.[key]?.counts as Record<string, number> | undefined;
	$: configCounts = settings?.[key]?.counts as { enable?: boolean; power_thr?: number } | undefined;
	$: countsEnabled = configCounts?.enable !== false;
	$: showPmCounters = hasActiveLoadRuntimeSupport(device, status);
	$: powerLimit = Number(settings?.[key]?.power_limit) || 0;
	$: powerThresholdMax =
		powerLimit > 0 ? powerLimit : (DEVICE_MAX_POWER_DEFAULTS[device.type] ?? 3600);

	$: if (configCounts && !powerThresholdInitialized) {
		powerThresholdInput = String(configCounts.power_thr ?? 0);
		powerThresholdInitialized = true;
	}

	$: thresholdNum = Number(powerThresholdInput);
	$: inputData = getErrorOrInfoMessageForMinAndMax(
		thresholdNum,
		powerThresholdMin,
		powerThresholdMax,
		true,
	);
	$: powerThresholdSaveDisabled =
		loadingThreshold || isOffline || powerThresholdInput === '' || !!inputData.error;

	const mergeStatusFromGetStatus = async () => {
		const res: SettingRequest = await gen2Request(device, `${comp}.GetStatus`, {
			id: componentIndex,
		});
		if (!res?.isok || res.data == null) return;
		const cur = readDeviceStatus(realDeviceId) || {};
		const next = mergePartialDeviceStatus(cur as Record<string, unknown>, { [key]: res.data });
		devices_status.update_device_status({ id: realDeviceId, msg: next });
	};

	onMount(() => {
		if (!isOffline) void mergeStatusFromGetStatus();
	});

	const persistCounts = async (partial: {
		enable?: boolean;
		power_thr?: number;
	}): Promise<boolean> => {
		const s = readDeviceSettings(realDeviceId);
		const prev = (s?.[key]?.counts || {}) as { enable?: boolean; power_thr?: number };
		const counts = { ...prev, ...partial };
		const res: SettingRequest = await gen2Request(device, `${comp}.SetConfig`, {
			id: componentIndex,
			config: { counts },
		});
		if (res?.isok) {
			s[key] = { ...s[key], counts };
			devices_settings.update_devices_settings(realDeviceId, s);
		}
		onSettingsResponse(res, device);
		return !!res?.isok;
	};

	const onToggleEnable = async (e: InputClickEvent) => {
		if (loadingToggle || isOffline) return;
		const next = e.target.checked;
		loadingToggle = true;
		const ok = await persistCounts({ enable: next });
		loadingToggle = false;
		if (ok) await mergeStatusFromGetStatus();
	};

	const savePowerThreshold = async () => {
		if (loadingThreshold || isOffline || powerThresholdSaveDisabled) return;
		const v = Math.floor(thresholdNum);
		loadingThreshold = true;
		const ok = await persistCounts({ power_thr: v });
		loadingThreshold = false;
		if (ok) await mergeStatusFromGetStatus();
	};

	const openReset = (t: CounterType) => {
		resetModalType = t;
	};

	const openResetAll = () => {
		resetModalType = 'all';
	};

	const getResetTypes = (modalType: typeof resetModalType): CounterType[] => {
		if (modalType === 'all') {
			return showPmCounters ? ['on_time', 'switch_on', 'on_above_thr'] : ['on_time', 'switch_on'];
		}
		return [modalType as CounterType];
	};

	const confirmReset = async () => {
		if (!resetModalType || loadingReset || isOffline) return;
		loadingReset = true;
		const types = getResetTypes(resetModalType);
		const res: SettingRequest = await gen2Request(device, `${comp}.ResetCounters`, {
			id: componentIndex,
			type: types,
		});
		if (res?.isok) {
			resetModalType = null;
			await mergeStatusFromGetStatus();
		}
		onSettingsResponse(res, device);
		loadingReset = false;
	};

	const getResetTitleKey = (modalType: typeof resetModalType): string => {
		if (modalType === 'all') return 'operational_counters_reset_modal_all';
		if (modalType === 'on_time') return 'operational_counters_reset_modal_on_time';
		if (modalType === 'switch_on') return 'operational_counters_reset_modal_switch_on';
		if (modalType === 'on_above_thr') return 'operational_counters_reset_modal_on_above_thr';
		return '';
	};

	$: resetTitleKey = getResetTitleKey(resetModalType);
</script>

<div class="mb-m d-flex align-items-center justify-content-between flex-wrap gap-s">
	<Text
		styleType="controls-small"
		tag="span"
		id="op-counters-master-label"
		class={countsEnabled ? 'text-bold' : ''}
	>
		{$format('operational_counters_enable_master', {
			default: 'Enable operational counters',
		})}
	</Text>
	<Toggle
		checked={countsEnabled}
		size="m"
		disabled={loadingToggle || isOffline}
		on:click={onToggleEnable}
		id="toggle-operational-counters-enable"
		aria-labelledby="op-counters-master-label"
	/>
</div>

{#if showPmCounters}
	<Text class="mb-xs" styleType="controls-small" tag="label" for="op-counters-power-thr">
		{$format('operational_counters_power_thr_label', {
			default: 'Active load threshold (W)',
		})}
	</Text>
	<Input
		{...getNumberInputConfig()}
		id="op-counters-power-thr"
		size="m"
		class="mb-s"
		icon="fas fa-calculator"
		bind:value={powerThresholdInput}
		step={1}
		disabled={isOffline}
		error={inputData.error}
		info={inputData.info}
		aria-label={$format('operational_counters_power_thr_label', {
			default: 'Active load threshold (W)',
		})}
	/>
	<Button
		size="m"
		width="full"
		class="mb-m"
		loading={loadingThreshold}
		disabled={powerThresholdSaveDisabled}
		on:click={savePowerThreshold}
	>
		{$format('button_save', { default: 'Save' })}
	</Button>
{/if}

{#if countsEnabled}
	<div class="d-flex flex-direction-column gap-m">
		<div class="d-flex justify-content-between align-items-center">
			<div>
				<Text class="mb-s" styleType="controls-small">
					{$format('operational_counters_on_time', { default: 'Total runtime' })}
				</Text>
				<Tooltip title={formatOperationalRuntimeFull(statusCounts?.on_time)} placement="top">
					<Text styleType="body">
						{formatOperationalRuntimeSeconds(statusCounts?.on_time)}
					</Text>
				</Tooltip>
			</div>
			<div class="d-flex flex-direction-column align-items-end gap-s">
				<Button
					size="s"
					variant="secondary"
					disabled={isOffline}
					on:click={() => openReset('on_time')}
				>
					{capFirstChar($format('button_reset', { default: 'Reset' }))}
				</Button>
				{#if statusCounts?.on_time_rst_ts}
					<Text style="text-align: end;" styleType="tiny">
						{$format('operational_counters_last_reset', {
							default: 'Last reset: {t}',
							values: {
								t: formatOperationalResetTimestamp(statusCounts?.on_time_rst_ts),
							},
						})}
					</Text>
				{/if}
			</div>
		</div>
		<hr class="divider" />

		<div class="d-flex justify-content-between align-items-center">
			<div>
				<Text class="mb-s" styleType="controls-small">
					{$format('operational_counters_switch_on', { default: 'Switching cycles' })}
				</Text>
				<Text styleType="body">{statusCounts?.switch_on ?? 0}</Text>
			</div>
			<div class="d-flex flex-direction-column align-items-end gap-s">
				<Button
					size="s"
					variant="secondary"
					disabled={isOffline}
					on:click={() => openReset('switch_on')}
				>
					{capFirstChar($format('button_reset', { default: 'Reset' }))}
				</Button>
				{#if statusCounts?.switch_on_rst_ts}
					<Text style="text-align: end;" styleType="tiny">
						{$format('operational_counters_last_reset', {
							default: 'Last reset: {t}',
							values: {
								t: formatOperationalResetTimestamp(statusCounts?.switch_on_rst_ts),
							},
						})}
					</Text>
				{/if}
			</div>
		</div>
		<hr class="divider" />

		{#if showPmCounters}
			<div class="d-flex justify-content-between align-items-center">
				<div>
					<Text class="mb-s" styleType="controls-small">
						{$format('operational_counters_on_above_thr', {
							default: 'Active load runtime',
						})}
					</Text>
					<Tooltip
						title={formatOperationalRuntimeFull(statusCounts?.on_above_thr)}
						placement="top"
					>
						<Text styleType="body">
							{formatOperationalRuntimeSeconds(statusCounts?.on_above_thr)}
						</Text>
					</Tooltip>
				</div>
				<div class="d-flex flex-direction-column align-items-end gap-s">
					<Button
						size="s"
						variant="secondary"
						disabled={isOffline}
						on:click={() => openReset('on_above_thr')}
					>
						{capFirstChar($format('button_reset', { default: 'Reset' }))}
					</Button>
					{#if statusCounts?.on_above_thr_rst_ts}
						<Text style="text-align: end;" styleType="tiny">
							{$format('operational_counters_last_reset', {
								default: 'Last reset: {t}',
								values: {
									t: formatOperationalResetTimestamp(
										statusCounts?.on_above_thr_rst_ts,
									),
								},
							})}
						</Text>
					{/if}
				</div>
			</div>
		{/if}

		<Button size="m" width="full" variant="secondary" disabled={isOffline} on:click={openResetAll}>
			{capFirstChar($format('operational_counters_reset_all', { default: 'Reset all' }))}
		</Button>
	</div>
{/if}

{#if resetModalType}
	<Modal
		showModal={true}
		on:close={() => (resetModalType = null)}
		title={capFirstChar($format('button_reset', { default: 'reset' })) +
			' ' +
			$format(resetTitleKey, { default: 'Counter' })}
	>
		<svelte:fragment slot="content">
			<Container padding="0">
				<div class="d-flex flex-direction-column align-items-center">
					<Icon icon="text-danger fak fa-light-factory-reset" size="xxl" />
					<Text class="mt-xs width-full">
						{#if resetModalType === 'all'}
							{$format('operational_counters_reset_all_confirm', {
								default:
									'This action cannot be undone. All counters will be reset to zero.',
							})}
						{:else}
							{$format('operational_counters_reset_confirm', {
								default:
									'Only this counter will be reset. Other counters will remain unchanged.',
							})}
						{/if}
					</Text>
				</div>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{ sm: 'm' }}
				variant="secondary"
				on:click={() => (resetModalType = null)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{ sm: 'm' }}
				loading={loadingReset}
				disabled={loadingReset}
				on:click={confirmReset}
			>
				{capFirstChar($format('button_reset', { default: 'reset' }))}
			</Button>
		</div>
	</Modal>
{/if}
